import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

/**
 * AnimatedModal – modal wrapper with fade transitions.
 * Backdrop and content fade in and out.
 *
 * Locks background body scroll while open and prevents scroll-chaining from
 * the modal's internal scroll container to the page behind it. Uses the shared
 * `useBodyScrollLock` hook so behavior is consistent with hand-rolled modals
 * elsewhere in the app (nested/stacked modals compose correctly).
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {React.ReactNode} children – modal content (the white card/panel)
 * @param {string} [contentClassName] – extra classes for the content wrapper
 * @param {string} [backdropClassName] – extra classes for the backdrop
 * @param {number} [zIndex=50]
 * @param {string} [ariaLabelledBy] – id of the element that labels the dialog
 * @param {'center' | 'right-panel'} [variant='center'] – layout/animation style
 * @param {boolean} [closeOnBackdrop=true] – false for long forms, where a stray click outside
 *   the card would throw away what the user typed; they close with the card's own buttons
 */
const AnimatedModal = ({
  isOpen,
  onClose,
  children,
  contentClassName = '',
  backdropClassName = '',
  zIndex = 50,
  ariaLabelledBy,
  variant = 'center',
  overflowHidden = false,
  closeOnBackdrop = true,
}) => {
  const isRightPanel = variant === 'right-panel';

  useBodyScrollLock(isOpen);

  const containerClasses = isRightPanel
    ? 'flex min-h-full items-stretch justify-end text-center sm:p-0'
    : 'flex min-h-full items-center justify-center p-4 text-center sm:p-0';

  const contentBaseClasses = isRightPanel
    ? 'relative inline-block h-full w-full transform text-left align-middle shadow-xl transition-all'
    : 'relative inline-block w-full transform text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle';

  const contentMotionProps = isRightPanel
    ? {
        initial: { opacity: 0, x: 24 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 24 },
      }
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      };

  // Portaled to <body>: a modal opened from inside a table row would otherwise inherit the row's
  // opacity and be trapped under sticky columns and later rows, whatever its zIndex.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className={`fixed inset-0 ${overflowHidden ? 'overflow-hidden' : 'overflow-y-auto'}`}
          style={{ zIndex, overscrollBehavior: 'contain' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
        >
          <div className={containerClasses}>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`fixed inset-0 bg-black/40 backdrop-blur-[2px] ${backdropClassName}`}
              onClick={closeOnBackdrop ? onClose : undefined}
              aria-hidden
            />
            {/* Content wrapper – fade or slide in depending on variant */}
            <motion.div
              {...contentMotionProps}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`${contentBaseClasses} ${contentClassName}`}
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AnimatedModal;
